// src/components/Referral.jsx
//
// Esdras Referral Component
//
// USER reward:    +1 free scan per confirmed new-user signup you refer
// STYLIST reward: Platform commission reduction, up to 3% (1% per 5 referrals)

import React, { useState, useEffect } from 'react';
import { getAuth }           from 'firebase/auth';
import { doc, getDoc }       from 'firebase/firestore';
import { db }                from '../firebase';

const NAVY = '#001F3F';
const GOLD = '#B8860B';
const DARK = '#010810';

export default function Referral() {
  const [copied,   setCopied]   = useState(false);
  const [role,     setRole]     = useState('user');
  const [userData, setUserData] = useState({});
  const [loading,  setLoading]  = useState(true);

  const auth = getAuth();
  const user = auth.currentUser;

  useEffect(() => {
    if (!user) return;
    (async () => {
      const userSnap = await getDoc(doc(db, 'users', user.uid));
      if (userSnap.exists()) {
        setRole('user');
        setUserData(userSnap.data());
      } else {
        const barberSnap = await getDoc(doc(db, 'barbers', user.uid));
        if (barberSnap.exists()) {
          setRole('stylist');
          setUserData(barberSnap.data());
        }
      }
      setLoading(false);
    })();
  }, [user]);

  if (!user || loading) return null;

  const referralLink = `https://esdras-app-frontend.vercel.app/?ref=${user.uid}`;

  // ── USER STATS ──────────────────────────────────────────────────────────
  const totalReferrals = userData.successfulReferrals || 0;
  const bonusScans     = userData.bonusScans           || 0;  // 1 per referral

  // ── STYLIST STATS ───────────────────────────────────────────────────────
  // 1% reduction per 5 referrals, hard cap 3%
  const commissionReduction  = Math.min(Math.floor(totalReferrals / 5), 3);
  const effectiveCommission  = 10 - commissionReduction;
  const nextMilestone        = 5 - (totalReferrals % 5);  // refs until next 1% drop

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const shareLink = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Join Esdras — Precision Grooming',
        text:  role === 'user'
          ? 'Join me on Esdras — the AI precision grooming platform. Use my link to sign up!'
          : 'Book your next session on Esdras — Lagos\'s top grooming platform.',
        url: referralLink,
      }).catch(() => {});
    } else {
      copyLink();
    }
  };

  return (
    <div style={wrapper}>
      {/* Header */}
      <div style={headerSection}>
        <p style={eyebrow}>ESDRAS REFERRAL PROGRAM</p>
        <h2 style={title}>GROW YOUR<br /><span style={{ color: GOLD }}>INNER CIRCLE</span></h2>
        <p style={subtitle}>
          {role === 'user'
            ? 'Invite friends to Esdras. Every confirmed signup earns you one extra free AI scan — no subscription needed.'
            : 'Grow the Esdras community. Earn platform commission reductions with every confirmed stylist or client signup.'}
        </p>
      </div>

      {/* Stats row */}
      <div style={statsRow}>
        <div style={statCard}>
          <p style={statNum}>{totalReferrals}</p>
          <p style={statLabel}>CONFIRMED REFERRALS</p>
        </div>

        {role === 'user' ? (
          <div style={{ ...statCard, borderColor: bonusScans > 0 ? GOLD : 'rgba(255,255,255,0.08)' }}>
            <p style={{ ...statNum, color: bonusScans > 0 ? GOLD : 'rgba(255,255,255,0.4)' }}>
              +{bonusScans}
            </p>
            <p style={statLabel}>BONUS SCANS EARNED</p>
          </div>
        ) : (
          <div style={{ ...statCard, borderColor: commissionReduction > 0 ? '#2ecc71' : 'rgba(255,255,255,0.08)' }}>
            <p style={{ ...statNum, color: commissionReduction > 0 ? '#2ecc71' : 'rgba(255,255,255,0.4)' }}>
              {effectiveCommission}%
            </p>
            <p style={statLabel}>YOUR PLATFORM FEE</p>
          </div>
        )}
      </div>

      {/* Progress / reward explanation */}
      <div style={rewardCard}>
        {role === 'user' ? (
          <>
            <p style={rewardTitle}>⚡ HOW YOU EARN</p>
            <p style={rewardText}>
              Each friend who signs up using your link and verifies their account
              grants you <strong style={{ color: GOLD }}>1 free AI scan</strong>.
              Bonus scans stack — invite 5 friends, get 5 scans.
            </p>
            <div style={progressRow}>
              <div style={progressBar}>
                <div style={{ ...progressFill, width: `${Math.min(totalReferrals * 20, 100)}%` }} />
              </div>
              <span style={progressLabel}>{totalReferrals} referred</span>
            </div>
          </>
        ) : (
          <>
            <p style={rewardTitle}>📉 COMMISSION REDUCTION</p>
            <p style={rewardText}>
              Every 5 confirmed signups reduces your platform fee by 1%,
              up to a maximum of <strong style={{ color: GOLD }}>3% off</strong> (standard rate: 10%).
            </p>
            <div style={milestonesRow}>
              {[5, 10, 15].map((n, i) => {
                const reached = totalReferrals >= n;
                return (
                  <div key={n} style={{ ...milestone, borderColor: reached ? GOLD : 'rgba(255,255,255,0.1)', background: reached ? 'rgba(184,134,11,0.12)' : 'transparent' }}>
                    <span style={{ fontSize: '0.9rem' }}>{reached ? '✓' : `${n - totalReferrals > 0 ? n - totalReferrals : 0} left`}</span>
                    <span style={{ fontSize: '0.55rem', color: reached ? GOLD : 'rgba(255,255,255,0.4)', letterSpacing: '1px', fontWeight: '900' }}>
                      -{i + 1}% AT {n} REFS
                    </span>
                  </div>
                );
              })}
            </div>
            {commissionReduction < 3 && (
              <p style={{ fontSize: '0.62rem', color: GOLD, opacity: 0.7, marginTop: '10px', margin: '10px 0 0' }}>
                {nextMilestone} more referral{nextMilestone !== 1 ? 's' : ''} until your next 1% reduction
              </p>
            )}
            {commissionReduction === 3 && (
              <p style={{ fontSize: '0.62rem', color: '#2ecc71', marginTop: '10px' }}>
                ✓ Maximum reduction achieved — you're at 7% platform fee
              </p>
            )}
          </>
        )}
      </div>

      {/* Link box */}
      <div style={linkSection}>
        <p style={{ fontSize: '0.58rem', color: GOLD, fontWeight: '900', letterSpacing: '2px', marginBottom: '10px' }}>
          YOUR REFERRAL LINK
        </p>
        <div style={linkBox}>
          <span style={linkText}>{referralLink}</span>
        </div>
        <div style={btnRow}>
          <button onClick={copyLink} style={copyBtn}>
            {copied ? '✓ COPIED!' : 'COPY LINK'}
          </button>
          <button onClick={shareLink} style={shareBtn}>
            ↑ SHARE
          </button>
        </div>
      </div>

      {/* Fine print */}
      <p style={finePrint}>
        {role === 'user'
          ? 'Bonus scans are credited instantly upon your referral\'s verified signup. Scans are available for use in any session.'
          : 'Commission reductions are applied to monthly payouts. Referral counts are verified against confirmed accounts only.'}
      </p>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const wrapper       = { background: 'rgba(255,255,255,0.02)', border: `1px solid rgba(184,134,11,0.25)`, borderRadius: '28px', padding: '2rem', maxWidth: '680px', margin: '0 auto', fontFamily: 'Montserrat, sans-serif' };
const headerSection = { textAlign: 'center', marginBottom: '2rem' };
const eyebrow       = { fontSize: '0.58rem', color: GOLD, letterSpacing: '3px', fontWeight: '900', margin: '0 0 10px' };
const title         = { color: 'white', fontSize: '2rem', fontWeight: '900', margin: '0 0 12px', lineHeight: 1.2 };
const subtitle      = { color: 'rgba(255,255,255,0.55)', fontSize: '0.82rem', lineHeight: 1.7, margin: 0 };
const statsRow      = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '1.5rem' };
const statCard      = { background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '1.4rem', textAlign: 'center' };
const statNum       = { fontSize: '2.4rem', fontWeight: '900', color: GOLD, margin: '0 0 4px' };
const statLabel     = { fontSize: '0.52rem', color: 'rgba(255,255,255,0.45)', letterSpacing: '1.5px', fontWeight: '700', margin: 0 };
const rewardCard    = { background: `rgba(184,134,11,0.05)`, border: `1px solid rgba(184,134,11,0.18)`, borderRadius: '20px', padding: '1.4rem', marginBottom: '1.5rem' };
const rewardTitle   = { fontSize: '0.62rem', color: GOLD, fontWeight: '900', letterSpacing: '2px', margin: '0 0 8px' };
const rewardText    = { fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, margin: '0 0 14px' };
const progressRow   = { display: 'flex', alignItems: 'center', gap: '12px' };
const progressBar   = { flex: 1, height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '999px', overflow: 'hidden' };
const progressFill  = { height: '100%', background: `linear-gradient(90deg, ${GOLD}, #ffdd88)`, borderRadius: '999px', transition: 'width 0.6s ease' };
const progressLabel = { fontSize: '0.6rem', color: GOLD, fontWeight: '700', whiteSpace: 'nowrap' };
const milestonesRow = { display: 'flex', gap: '10px' };
const milestone     = { flex: 1, border: '1px solid', borderRadius: '12px', padding: '10px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', color: 'white', fontSize: '0.75rem', fontWeight: '700' };
const linkSection   = { marginBottom: '1.5rem' };
const linkBox       = { background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', padding: '14px 16px', marginBottom: '12px', overflowX: 'auto' };
const linkText      = { fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', wordBreak: 'break-all', fontFamily: 'monospace' };
const btnRow        = { display: 'flex', gap: '10px' };
const copyBtn       = { flex: 1, background: GOLD, color: 'black', border: 'none', padding: '14px', borderRadius: '50px', fontWeight: '900', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' };
const shareBtn      = { background: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.15)', padding: '14px 24px', borderRadius: '50px', fontWeight: '700', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' };
const finePrint     = { fontSize: '0.62rem', color: 'rgba(255,255,255,0.3)', lineHeight: 1.7, textAlign: 'center', margin: 0 };
